import { useMemo, useState } from 'react';

export interface MemoryItem {
  id: string;
  title: string;
  date: Date;
}

interface MemoryCalendarProps {
  memories: MemoryItem[];
}

const WEEKDAYS = ['일', '월', '화', '수', '목', '금', '토'];

function ymKey(d: Date): string {
  return `${d.getFullYear()}-${d.getMonth()}`;
}

function dayKey(d: Date): string {
  return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
}

/**
 * 추억 캘린더: 종료된 모임(추억)을 월 그리드에 점으로 표시.
 * 날짜 선택 시 해당 날의 추억 목록을 아래에 보여준다.
 */
export function MemoryCalendar({ memories }: MemoryCalendarProps) {
  const latest = memories.length > 0
    ? memories.reduce((a, b) => (a.date > b.date ? a : b)).date
    : new Date();
  const [viewYear, setViewYear] = useState(latest.getFullYear());
  const [viewMonth, setViewMonth] = useState(latest.getMonth());
  const [selectedDay, setSelectedDay] = useState<string | null>(null);

  const byDay = useMemo(() => {
    const map = new Map<string, MemoryItem[]>();
    for (const m of memories) {
      const key = dayKey(m.date);
      map.set(key, [...(map.get(key) ?? []), m]);
    }
    return map;
  }, [memories]);

  const monthCells = useMemo(() => {
    const first = new Date(viewYear, viewMonth, 1);
    const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();
    const cells: Array<{ day: number; key: string } | null> = [];
    for (let i = 0; i < first.getDay(); i += 1) cells.push(null);
    for (let d = 1; d <= daysInMonth; d += 1) {
      cells.push({ day: d, key: dayKey(new Date(viewYear, viewMonth, d)) });
    }
    return cells;
  }, [viewYear, viewMonth]);

  function moveMonth(delta: number) {
    const next = new Date(viewYear, viewMonth + delta, 1);
    setViewYear(next.getFullYear());
    setViewMonth(next.getMonth());
    setSelectedDay(null);
  }

  const selected = selectedDay ? (byDay.get(selectedDay) ?? []) : [];
  const monthHasMemories = memories.some((m) => ymKey(m.date) === `${viewYear}-${viewMonth}`);

  return (
    <div>
      <div className="flex items-center justify-between">
        <button
          type="button"
          onClick={() => moveMonth(-1)}
          aria-label="이전 달"
          className="h-8 w-8 rounded-full text-ink-muted hover:bg-surface-subtle focus-visible:outline-none focus-visible:shadow-focus-ring"
        >
          ‹
        </button>
        <p className="text-ui font-semibold text-ink" aria-live="polite">
          {viewYear}년 {viewMonth + 1}월
        </p>
        <button
          type="button"
          onClick={() => moveMonth(1)}
          aria-label="다음 달"
          className="h-8 w-8 rounded-full text-ink-muted hover:bg-surface-subtle focus-visible:outline-none focus-visible:shadow-focus-ring"
        >
          ›
        </button>
      </div>

      <div className="mt-3 grid grid-cols-7 text-center">
        {WEEKDAYS.map((w) => (
          <span key={w} className="py-1 text-micro font-medium text-ink-muted">
            {w}
          </span>
        ))}
        {monthCells.map((cell, i) =>
          cell === null ? (
            <span key={`empty-${i}`} aria-hidden="true" />
          ) : (
            <button
              key={cell.key}
              type="button"
              disabled={!byDay.has(cell.key)}
              aria-pressed={selectedDay === cell.key}
              onClick={() =>
                setSelectedDay(selectedDay === cell.key ? null : cell.key)
              }
              className={`relative mx-auto my-0.5 flex h-9 w-9 items-center justify-center rounded-full text-body transition-colors duration-150 focus-visible:outline-none focus-visible:shadow-focus-ring ${
                selectedDay === cell.key
                  ? 'bg-primary text-surface font-semibold'
                  : byDay.has(cell.key)
                    ? 'bg-primary-soft text-ink font-medium hover:bg-primary/20'
                    : 'text-ink-muted'
              }`}
            >
              {cell.day}
              {byDay.has(cell.key) && selectedDay !== cell.key && (
                <span
                  aria-hidden="true"
                  className="absolute bottom-1 h-1 w-1 rounded-full bg-primary"
                />
              )}
            </button>
          ),
        )}
      </div>

      {!monthHasMemories && (
        <p className="mt-2 text-center text-small text-ink-muted">
          이 달에는 추억이 없어요.
        </p>
      )}

      {selected.length > 0 && (
        <ul className="mt-3 space-y-1.5" aria-label="선택한 날의 추억">
          {selected.map((m) => (
            <li
              key={m.id}
              className="rounded-card bg-surface-subtle px-3 py-2 text-body text-ink"
            >
              {m.title}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
